import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useTransition,
} from "react";
import {
  Table,
  Input,
  Button,
  Card,
  Row,
  Col,
  Dropdown,
  Menu,
  Space,
  DatePicker,
  Typography,
  Image,
  Tabs,
  Tooltip,
  Tag,
  message,
} from "antd";
import {
  SearchOutlined,
  EyeOutlined,
  ExportOutlined,
  CloseOutlined,
  CheckOutlined,
  HourglassOutlined,
  DownloadOutlined,
  SelectOutlined,
  PrinterOutlined,
  LeftOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  WarningFilled,
  RightOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import Dashboard from "../../../layouts/Dashboard";
import ReactLoading from "react-loading";
import moment from "moment";
import useAxios from "../../../hooks/useAxios";
import { useParams } from "react-router-dom/cjs/react-router-dom";
import MetaDataTab from "../components/MetaDataTab";
import { capitalizeEachWord } from "../../../utils/commonFunctions";
import swal from "sweetalert";
import { useHistory } from "react-router-dom";
import printJS from "print-js";
import SkeletonLoader from "../components/SkeletonLoader";
import RenderFiles from "../../CandidateDashboard/components/RenderFiles";
import { checkBulkPermissions } from "../../../utils/checkPermission";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const caseConversion = (string) => {
  return string?.replace(/([A-Z])/g, " $1").replace(/^./, function (str) {
    return str?.toUpperCase();
  });
};

function formatFileSize(bytes) {
  const sizeInKB = bytes / 1024;
  if (sizeInKB >= 1024) {
    const sizeInMB = sizeInKB / 1024;
    return `${sizeInMB.toFixed(2)} MB`;
  } else {
    return `${sizeInKB.toFixed(2)} KB`;
  }
}

const CentralHrAuditPage = () => {
  const api = useAxios();
  const history = useHistory();
  const { empId, candidateId } = useParams();
  const [documents, setDocuments] = useState([]);
  const [load, setLoad] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("Pending");
  const [docExtension, setDocExtension] = useState("");
  const [activeDoc, setActiveDoc] = useState(1);
  const [size, setSize] = useState(1);
  const [activeDocData, setActiveDocData] = useState(null);
  const [candidateDetails, setCandidateDetails] = useState(null);
  const [rejectionReason, setRejectionReason] = useState(null);
  const [isRejectEnable, setIsRejectEnable] = useState(false);
  const [candidate, setCandidate] = useState(null);
  const [auditDetailsDocumentCount, setAuditDetailsDocumentCount] = useState(0);
  const [fileblob, setFileBlob] = useState("");
  const [pending, startTransition] = useTransition();
  const [docVerification, setDocVerification] = useState(null);
  const [permissions, setPermissions] = useState({
    canView: true, canAccept: false, canReject: false,
    canSubmitReview: false, canDownload: false, canPrint: false,
  });

  useEffect(() => {
    const fetchPerms = async () => {
      const role = localStorage.getItem("user.role");
      if (!role) return;
      const checks = [
        { entityType: "central_hr_audit_document_module", permission: "view_central_hr_audit_document" },
        { entityType: "central_hr_audit_document_module", permission: "accept_central_hr_audit_document" },
        { entityType: "central_hr_audit_document_module", permission: "reject_central_hr_audit_document" },
        { entityType: "central_hr_audit_document_module", permission: "submit_central_hr_audit_document" },
        { entityType: "central_hr_audit_document_module", permission: "download_central_hr_audit_document" },
        { entityType: "central_hr_audit_document_module", permission: "print_central_hr_audit_document" },
      ];
      try {
        const results = await checkBulkPermissions(role, checks);
        const isAllowed = (perm) => results.find((r) => r.permission === perm)?.allowed || false;
        setPermissions({
          canView: isAllowed("view_central_hr_audit_document"),
          canAccept: isAllowed("accept_central_hr_audit_document"),
          canReject: isAllowed("reject_central_hr_audit_document"),
          canSubmitReview: isAllowed("submit_central_hr_audit_document"),
          canDownload: isAllowed("download_central_hr_audit_document"),
          canPrint: isAllowed("print_central_hr_audit_document"),
        });
      } catch (err) { console.log(err); }
    };
    fetchPerms();
  }, []);

  const getAllDocuments = async () => {
    try {
      const result = await api.get(
        `/makercheaker/AllCandidateDocuments/${empId}`,
        { tokenType: "user" },
      );
      let srces = [];
      const {
        bankDetails,
        candidateDocuments,
        personalDetails,
        companyDocuments,
        candidateDetails,
        auditDetails,
        docuemntVerifiedDetails,
      } = result.data;
      const {
        aadharCardImage,
        candidatePhoto,
        candidateSignature,
        panCardImage,
        aadharStatus,
        panStatus,
        candidatePhotoStatus,
        candidateSignatureStatus,
        _id,
      } = personalDetails[0];

      if (aadharCardImage?.length !== 0) {
        srces.push({
          path: aadharCardImage[0].path,
          docData: aadharCardImage[0],
          name: caseConversion(aadharCardImage[0]?.fieldname),
          type: "Aadhar_Card",
          documentId: "",
          auditingDetails: aadharCardImage[0]?.auditingDetails,
          isDocumentVerifiedByHr: aadharStatus,
        });
      }
      if (candidatePhoto?.length !== 0) {
        srces.push({
          path: candidatePhoto[0].path,
          docData: candidatePhoto[0],
          name: caseConversion(candidatePhoto[0]?.fieldname),
          type: "Profile_Image",
          documentId: "",
          auditingDetails: candidatePhoto[0]?.auditingDetails,
          isDocumentVerifiedByHr: candidatePhotoStatus,
        });
      }
      if (candidateSignature?.length !== 0) {
        srces.push({
          path: candidateSignature[0].path,
          docData: candidateSignature[0],
          name: caseConversion(candidateSignature[0]?.fieldname),
          type: "Signature",
          documentId: "",
          auditingDetails: candidateSignature[0]?.auditingDetails,
          isDocumentVerifiedByHr: candidateSignatureStatus,
        });
      }
      if (panCardImage?.length !== 0) {
        srces.push({
          path: panCardImage[0].path,
          docData: panCardImage[0],
          name: caseConversion(panCardImage[0]?.fieldname),
          type: "Pan_Card",
          documentId: "",
          auditingDetails: panCardImage[0]?.auditingDetails,
          isDocumentVerifiedByHr: panStatus,
        });
      }

      if (bankDetails?.length !== 0) {
        const { files: banksFiles, documentStatus } = bankDetails[0];
        for (let key of banksFiles) {
          srces.push({
            path: key.path,
            docData: key,
            name: caseConversion(key?.fieldname),
            type: "Candidate_Document",
            documentId: bankDetails[0]?._id,
            auditingDetails: key?.auditingDetails,
            isDocumentVerifiedByHr: documentStatus,
          });
        }
      }

      if (candidateDocuments?.length !== 0) {
        for (let doc of candidateDocuments) {
          const { files: candidateFiltes, documentStatus, _id } = doc;
          for (let key of candidateFiltes) {
            srces.push({
              path: key.path,
              docData: key,
              name: caseConversion(key?.fieldname),
              type: "Candidate_Document",
              documentId: _id,
              auditingDetails: key?.auditingDetails,
              isDocumentVerifiedByHr: documentStatus,
            });
          }
        }
      }

      for (let key of companyDocuments) {
        const { files } = key;
        srces.push({
          path: key?.filePath,
          docData: files[0],
          name: key?.companyDocumentId?.formName,
          type: "Company_Document",
          documentId: key?._id,
          auditingDetails: key?.auditingDetails,
          isDocumentVerifiedByHr: key?.formStatusByHR,
        });
      }

      setDocuments(srces);
      setCandidate({ candidateDetails, personalDetails: personalDetails[0] });
      setAuditDetailsDocumentCount(auditDetails);
      setDocVerification(docuemntVerifiedDetails);
    } catch (err) {
      console.log(err);
    }
  };

  const getFileSize = async (url) => {
    try {
      setLoading(true);
      const response = await fetch(url, { mode: "cors" });
      const blob = await response.blob();
      const contentLength = response.headers.get("Content-Length");
      let sizeInBytes = contentLength ? parseInt(contentLength) : null;

      if (!sizeInBytes) {
        sizeInBytes = blob.size;
      }
      startTransition(() => {
        if (fileblob) URL.revokeObjectURL(fileblob);
        setFileBlob(URL.createObjectURL(blob));
      });
      if (sizeInBytes) {
        return formatFileSize(sizeInBytes);
      } else {
        return "N/A";
      }
    } catch (error) {
      return "N/A";
    } finally {
      setLoading(false);
    }
  };

  const downloadDoc = async (e, doc) => {
    try {
      setLoad(true);
      const res = await fetch(activeDocData?.path, {
        mode: "cors",
      });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${activeDocData?.name}.${docExtension}`;
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setLoad(false);
    } catch (err) {
      console.log(err);
      setLoad(false);
    }
  };

  const onClickPrint = () => {
    const ext = activeDocData?.docData?.originalname
      ?.split(".")
      .pop()
      ?.toLowerCase();
    if (["jpg", "png", "jpeg", "jfif"].includes(ext)) {
      printJS({
        printable: fileblob,
        type: "image",
        imageStyle: "object-fit:contain;",
        onError: () => {
          message.error("Something went wrong while print.", 3);
        },
      });
    } else if (ext === "pdf") {
      printJS({
        printable: fileblob,
        type: "pdf",
        onError: () => {
          message.error("Something went wrong while print.", 3);
        },
      });
    }
  };

  const openDocument = () => {
    let path = "";
    if (activeDocData?.type?.toLowerCase() === "company_document") {
      path = activeDocData?.docData?.path;
    } else {
      path = `${activeDocData?.docData?.destination}/${activeDocData?.docData?.filename}`;
    }
    window.open(`/doc/${encodeURIComponent(path)}`, "_blank");
  };

  const getCandidateDetails = async () => {
    try {
      const result = await api.get(
        `/candidateDocuments/candidateAllDetails/getCandidateDetails/${candidateId}`,
        { tokenType: "user" },
      );
      setCandidateDetails(result.data);
    } catch (err) {
      console.log(err);
    }
  };

  const getPendingStatus = (auditingDetails) => {
    if (auditingDetails === null) {
      return true;
    }
    if (
      auditingDetails?.documentStatusByCheker === false &&
      auditingDetails?.documentStatusByMaker === true
    ) {
      return true;
    }

    if (
      auditingDetails?.documentStatusByCheker === false &&
      auditingDetails?.documentStatusByMaker === false
    ) {
      return true;
    }
    if (
      auditingDetails?.documentStatusByCheker === null &&
      auditingDetails?.documentStatusByMaker === null
    ) {
      return true;
    }
  };

  const getCompletedStatus = (auditingDetails) => {
    if (
      auditingDetails?.documentStatusByCheker === true &&
      auditingDetails?.documentStatusByMaker === true
    ) {
      return true;
    }
    if (
      auditingDetails?.documentStatusByCheker === true &&
      auditingDetails?.documentStatusByMaker === null
    ) {
      return true;
    }
    if (
      auditingDetails?.documentStatusByCheker === false &&
      auditingDetails?.documentStatusByMaker === null
    ) {
      return true;
    }
  };

  const TAB_STATUS = candidate?.candidateDetails?.documentAuditStatus
    ? "Completed"
    : status;

  const filteredDoc = useMemo(() => {
    const arr = documents?.filter((ele) => {
      const { auditingDetails } = ele;
      if (TAB_STATUS === "Pending") {
        return getPendingStatus(auditingDetails);
      } else if (TAB_STATUS === "Completed") {
        return getCompletedStatus(auditingDetails);
      }
    });

    arr.sort(
      (a, b) =>
        a.auditingDetails?.documentStatusByCheker -
        b.auditingDetails?.documentStatusByCheker,
    );
    return arr;
  }, [documents, status]);

  useEffect(() => {
    if (filteredDoc.length === 0) return;

    const init = async () => {
      const _size = await getFileSize(filteredDoc[activeDoc - 1]?.path);
      startTransition(() => {
        setSize(_size);
      });
    };
    setActiveDocData(filteredDoc[activeDoc - 1]);

    if (filteredDoc[activeDoc - 1]) {
      const ext = filteredDoc[activeDoc - 1].docData?.filename
        ?.split(".")
        ?.pop()
        ?.toLowerCase();
      setDocExtension(ext);
      init();
    }
  }, [documents, activeDoc, status]);

  useEffect(() => {
    if (!empId || !candidateId) return;
    const init = async () => {
      try {
        setLoad(true);
        await Promise.all([getAllDocuments(), getCandidateDetails()]);
      } catch (err) {
        console.log(err);
      } finally {
        setLoad(false);
      }
    };
    init();
  }, [empId, candidateId]);

  const ALL_DOCUMENTS_APPROVED = useMemo(() => {
    let count = 0;
    const flag = documents?.every((ele) => {
      if (
        ele?.auditingDetails !== null &&
        ele?.auditingDetails?.documentStatusByCheker === true
      ) {
        count++;
        return true;
      }
    });
    return {
      flag,
      count,
    };
  }, [documents]);

  const documentStatusMark = async (status) => {
    if (!status) {
      const flag = await swal({
        title: "Warning",
        icon: "warning",
        content: {
          element: "div",
          attributes: {
            innerHTML: `<p style="text-align:center">Do you want to proceed ?</p>`,
          },
        },
        buttons: {
          cancel: true,
          confirm: true,
        },
      });
      if (!flag) return;
    }

    try {
      setLoad(true);
      await api.put(
        `/makercheaker/checker/candidateDocuments`,
        {
          fileId: activeDocData?.docData?._id || "",
          documentId: activeDocData?.documentId,
          documentStatus: status,
          rejectionReason: rejectionReason,
          candidateId: candidateId,
          documentType: activeDocData?.type,
        },
        { tokenType: "user" },
      );
      setIsRejectEnable(false);
      setRejectionReason(null);
      await getAllDocuments();
    } catch (err) {
      console.log(err);
      message.error("Something went wrong.");
    } finally {
      setLoad(false);
    }
  };

  const submitReview = async () => {
    const flag = await swal({
      title: "Warning",
      icon: "warning",
      content: {
        element: "div",
        attributes: {
          innerHTML: `<p style="text-align:center">Do you want to proceed ?</p>`,
        },
      },
      buttons: {
        cancel: true,
        confirm: true,
      },
    });

    if (!flag) return;

    try {
      setLoad(true);
      await api.put(
        `/makercheaker/checker/checkerSubmission`,
        {
          id: candidateId,
          auditStatus:
            ALL_DOCUMENTS_APPROVED?.count ===
            (auditDetailsDocumentCount?.documentCount || 0) +
            (auditDetailsDocumentCount?.PCFCount || 0),
        },
        { tokenType: "user" },
      );
      history.push(`/audit/central-hr`);
    } catch (err) {
      console.log(err);
    } finally {
      setLoad(false);
    }
  };

  const onClickUndo = async () => {
    try {
      setLoad(true);
      await api.put(
        `/makercheaker/checker/undo`,
        {
          fileId: activeDocData?.docData?._id || "",
          documentId: activeDocData?.documentId,
          documentStatus: null,
          rejectionReason: null,
          candidateId: candidateId,
          documentType: activeDocData?.type,
        },
        { tokenType: "user" },
      );
      await getAllDocuments();
      message.success("Undo Successfully");
    } catch (err) {
      console.log(err);
    } finally {
      setLoad(false);
    }
  };

  const NOT_TOUCH_YET = useMemo(() => {
    return documents?.every((ele) => ele?.auditingDetails === null);
  }, [documents]);

  const IS_DOCUMENT_ACCEPTED =
    activeDocData?.auditingDetails?.documentStatusByCheker === true;
  const IS_DOCUMENT_REJECTED =
    activeDocData?.auditingDetails?.documentStatusByCheker === false;

  const ENABLE_SUBMIT_REVIEW = documents?.every(
    (ele) => ele?.auditingDetails !== null,
  );

  const IS_PENDING_CLEAR = documents?.some((ele) => {
    const { auditingDetails } = ele;
    return getPendingStatus(auditingDetails) === true;
  });

  let REASON_BY_CENTRAL_HR = "";
  let REASON_BY_SITE_HR = "";
  let ACCEPT_REASON_BY_SITE_HR = "";
  let REASON_BY_CENTRAL_HR_COUNT = 0;
  let REASON_BY_SITE_HR_COUNT = 0;
  let ACCEPT_REASON_BY_SITE_HR_COUNT = 0;

  if (
    activeDocData?.auditingDetails?.rejectionRemarksByChecker !== null &&
    activeDocData?.auditingDetails?.rejectionRemarksByChecker?.length !== 0
  ) {
    REASON_BY_CENTRAL_HR =
      activeDocData?.auditingDetails?.rejectionRemarksByChecker[
      activeDocData?.auditingDetails?.rejectionRemarksByChecker?.length - 1
      ];
    REASON_BY_CENTRAL_HR_COUNT =
      activeDocData?.auditingDetails?.rejectionRemarksByChecker?.length;
  }

  if (
    activeDocData?.auditingDetails?.rejectionRemarksByMaker !== null &&
    activeDocData?.auditingDetails?.rejectionRemarksByMaker?.length !== 0
  ) {
    REASON_BY_SITE_HR =
      activeDocData?.auditingDetails?.rejectionRemarksByMaker[
      activeDocData?.auditingDetails?.rejectionRemarksByMaker?.length - 1
      ];
    REASON_BY_SITE_HR_COUNT =
      activeDocData?.auditingDetails?.rejectionRemarksByMaker?.length;
  }

  if (
    activeDocData?.auditingDetails?.acceptanceRemarksByMaker !== null &&
    activeDocData?.auditingDetails?.acceptanceRemarksByMaker?.length !== 0
  ) {
    ACCEPT_REASON_BY_SITE_HR =
      activeDocData?.auditingDetails?.acceptanceRemarksByMaker[
      activeDocData?.auditingDetails?.acceptanceRemarksByMaker?.length - 1
      ];
    ACCEPT_REASON_BY_SITE_HR_COUNT =
      activeDocData?.auditingDetails?.acceptanceRemarksByMaker?.length;
  }

  const CENTRAL_HR_NAME =
    activeDocData?.auditingDetails?.auditByUserId?.name || "Central HR";
  const SITE_HR_NAME =
    activeDocData?.auditingDetails?.approveByUserId?.name || "Site HR";
  const SUBMIT_REVIEW =
    candidate?.candidateDetails?.documentAuditStatus || false;

  const IS_DOCUMENT_VERIFIED_BY_HR = activeDocData?.isDocumentVerifiedByHr;
  const DESIGNATION =
    candidate?.personalDetails?.trf?.designationId?.designation || "N/A";
  const PROCESS = candidate?.personalDetails?.trf?.processName || "N/A";
  const IS_CANDIDATE_ACTIVE =
    candidate?.personalDetails?.IsCandidateActive || false;
  const DOB =
    candidate?.personalDetails?.responses?.find(
      (ele) => ele?.label === "Date of Birth",
    )?.value || "N/A";

  const IS_FIRST_CYCLE = auditDetailsDocumentCount?.totalSubmission === 0;

  if (!permissions.canView) {
    return (
      <Dashboard title="Documents">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <Title level={3}>Access Denied</Title>
        </div>
      </Dashboard>
    );
  }

  return (
    <Dashboard title="Documents">
      {load && (
        <ReactLoading type="spin" color="#0000FF" className="spinnerBg" />
      )}

      <div className="p-4 bg-white rounded">
        <div>
          <Card className="mb-3 review-tab">
            <div className="flex justify-content-between align-items-center">
              <div>
                <Title level={5} className="mb-0 fw-bold">
                  Document Review -{" "}
                  {capitalizeEachWord(
                    `${candidateDetails?.first_name} ${candidateDetails?.last_name}`,
                  )}{" "}
                  ({DESIGNATION} - {PROCESS})
                </Title>
                <Tag
                  className="mt-1"
                  color={IS_CANDIDATE_ACTIVE ? "success" : "error"}
                >
                  {IS_CANDIDATE_ACTIVE ? "Active" : "Inactive"}
                </Tag>
                {/* <Text>
                  Submitted Date: 5 days ago &nbsp;&nbsp;&nbsp; Days Remaining:
                  3 Days (Mar 29)
                </Text> */}
              </div>
              {permissions.canSubmitReview && (
                <button
                  onClick={submitReview}
                  disabled={
                    !ENABLE_SUBMIT_REVIEW || IS_PENDING_CLEAR || SUBMIT_REVIEW
                  }
                  className={`btn ${!ENABLE_SUBMIT_REVIEW || IS_PENDING_CLEAR || SUBMIT_REVIEW
                    ? "btn-secondary"
                    : "btn-primary"
                    }`}
                >
                  Submit Review
                </button>
              )}
            </div>
          </Card>
          <Card>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={6}>
                <div className="flex mb-2">
                  {!SUBMIT_REVIEW && (
                    <Button
                      onClick={(e) => {
                        setStatus("Pending");
                        setActiveDoc(1);
                      }}
                      type="text"
                      block
                      className="w-1/2 border font-semibold"
                      style={{
                        background:
                          TAB_STATUS === "Pending" ? "#0d1953" : "white",
                        color: TAB_STATUS === "Pending" ? "white" : "black",
                        height: status === "Pending" ? "2.5rem" : "2.5rem",
                      }}
                    >
                      Pending
                    </Button>
                  )}
                  <Button
                    onClick={(e) => {
                      setStatus("Completed");
                      setActiveDoc(1);
                    }}
                    type="text"
                    block
                    className="w-1/2 border"
                    style={{
                      background:
                        TAB_STATUS === "Completed" ? "#0d1953" : "white",
                      color: TAB_STATUS === "Completed" ? "white" : "black",
                      height: status === "Pending" ? "2.5rem" : "2.5rem",
                    }}
                  >
                    Completed
                  </Button>
                </div>
                <div className="border rounded bg-white  document-tabs">
                  {filteredDoc?.length === 0 ? (
                    <div className="mt-4 d-flex justify-content-center">
                      {" "}
                      <Title level={5} className="mb-1 fw-bold">
                        No Document {status}
                      </Title>
                    </div>
                  ) : (
                    <Tabs
                      activeKey={String(activeDoc)}
                      tabPosition="left"
                      defaultActiveKey="1"
                      style={{ height: "100%" }}
                      onChange={(e) => {
                        setIsRejectEnable(false);
                        setRejectionReason(null);
                        setActiveDoc(Number(e));
                      }}
                    >
                      {filteredDoc?.map((file, index) => {
                        const { auditingDetails } = file;
                        return (
                          <TabPane
                            tab={
                              <span
                                style={{
                                  display: "flex",
                                  gap: "0.5rem",
                                  justifyContent: "space-between",
                                  alignItems: "flex-end",
                                }}
                                className="text-green-600"
                              >
                                <span
                                  style={{
                                    display: "inline-block",
                                    width: "100%",
                                    textAlign: "left",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {file?.name}{" "}
                                </span>
                                {!NOT_TOUCH_YET ? (
                                  <>
                                    {auditingDetails === null ||
                                      getPendingStatus(auditingDetails) ? (
                                      <>
                                        <span
                                          style={{
                                            backgroundColor: "ffffff00",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            width: "25px",
                                            height: "25px",
                                            borderRadius: "50%",
                                          }}
                                          className="bg-red ro"
                                        >
                                          <HourglassOutlined
                                            style={{
                                              color: "#F28222",
                                              margin: "0px",
                                            }}
                                          />
                                        </span>
                                      </>
                                    ) : (
                                      <>
                                        {auditingDetails?.documentStatusByCheker ? (
                                          <>
                                            <span
                                              style={{
                                                backgroundColor: "#00AC1A",
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                width: "25px",
                                                height: "25px",
                                                borderRadius: "50%",
                                              }}
                                              className="bg-red ro"
                                            >
                                              <CheckOutlined
                                                style={{
                                                  color: "white",
                                                  margin: "0px",
                                                }}
                                              />
                                            </span>
                                          </>
                                        ) : (
                                          <>
                                            <span
                                              style={{
                                                backgroundColor: "red",
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                width: "25px",
                                                height: "25px",
                                                borderRadius: "50%",
                                              }}
                                              className="bg-red ro"
                                            >
                                              <CloseOutlined
                                                style={{
                                                  color: "white",
                                                  margin: "0px",
                                                }}
                                              />
                                            </span>
                                          </>
                                        )}
                                      </>
                                    )}
                                  </>
                                ) : (
                                  <></>
                                )}
                              </span>
                            }
                            key={index + 1}
                          />
                        );
                      })}
                    </Tabs>
                  )}
                </div>
              </Col>
              <Col xs={24} md={18}>
                {filteredDoc?.length !== 0 ? (
                  <div className="border rounded p-4">
                    {loading ? (
                      <>
                        <SkeletonLoader />
                      </>
                    ) : (
                      <>
                        <div className="flex justify-content-between align-items-center mb-2">
                          <div className="flex gap-3">
                            {permissions.canAccept && (
                              <button
                                className={`__btn px-3 text-white rounded font-weight-bold ${activeDocData?.auditingDetails === null ||
                                  TAB_STATUS === "Pending"
                                  ? !IS_DOCUMENT_VERIFIED_BY_HR
                                    ? "__disabled"
                                    : "__initial_accepted"
                                  : IS_DOCUMENT_ACCEPTED
                                    ? "__accepted"
                                    : "__disabled"
                                  }`}
                                disabled={
                                  (IS_DOCUMENT_REJECTED &&
                                    TAB_STATUS !== "Pending"
                                    ? true
                                    : !IS_DOCUMENT_VERIFIED_BY_HR) ||
                                  activeDocData?.auditingDetails
                                    ?.documentStatusByCheker === true
                                }
                                onClick={() => documentStatusMark(true)}
                              >
                                {" "}
                                <CheckOutlined
                                  style={{
                                    borderRadius: "50%",
                                    padding: 2,
                                    marginRight: "5px",
                                  }}
                                  className={
                                    activeDocData?.auditingDetails === null ||
                                      TAB_STATUS === "Pending"
                                      ? !IS_DOCUMENT_VERIFIED_BY_HR
                                        ? "__disable_icon"
                                        : "__initial_accepted_icon"
                                      : IS_DOCUMENT_ACCEPTED
                                        ? `__accepted_icon`
                                        : "__disable_icon"
                                  }
                                />
                                ACCEPTED
                              </button>
                            )}
                            {permissions.canReject && (
                              <button
                                className={`__btn px-3 text-white rounded font-weight-bold ${activeDocData?.auditingDetails === null ||
                                  TAB_STATUS === "Pending"
                                  ? !IS_DOCUMENT_VERIFIED_BY_HR
                                    ? "__disabled"
                                    : "__initial_rejected"
                                  : IS_DOCUMENT_REJECTED
                                    ? "__rejected"
                                    : "__disabled"
                                  }`}
                                danger
                                onClick={() => setIsRejectEnable(true)}
                                disabled={
                                  (IS_DOCUMENT_ACCEPTED &&
                                    TAB_STATUS !== "Pending"
                                    ? true
                                    : !IS_DOCUMENT_VERIFIED_BY_HR) ||
                                  activeDocData?.auditingDetails
                                    ?.documentStatusByCheker === true
                                }
                              >
                                <CloseOutlined
                                  style={{
                                    borderRadius: "50%",
                                    padding: 2,
                                    marginRight: "5px",
                                  }}
                                  className={
                                    activeDocData?.auditingDetails === null ||
                                      TAB_STATUS === "Pending"
                                      ? !IS_DOCUMENT_VERIFIED_BY_HR
                                        ? "__disable_icon"
                                        : "__initial_rejected_icon"
                                      : IS_DOCUMENT_REJECTED
                                        ? `__rejected_icon`
                                        : "__disable_icon"
                                  }
                                />
                                REJECTED
                              </button>
                            )}
                            {(IS_DOCUMENT_ACCEPTED || IS_DOCUMENT_REJECTED) &&
                              IS_FIRST_CYCLE && (
                                <Tooltip placement="top" title={"Undo"}>
                                  <UndoOutlined
                                    onClick={onClickUndo}
                                    style={{
                                      borderRadius: "50%",
                                      padding: 2,
                                      marginRight: "5px",
                                      color: "#000",
                                      fontSize: "1.2rem",
                                    }}
                                  />
                                </Tooltip>
                              )}
                            {IS_DOCUMENT_REJECTED && (
                              <Tooltip
                                placement="top"
                                title={REASON_BY_CENTRAL_HR}
                              >
                                <InfoCircleOutlined
                                  style={{
                                    color: "red",
                                    fontSize: "1.1rem",
                                  }}
                                />
                              </Tooltip>
                            )}
                          </div>
                          <div className="flex gap-2">
                            {permissions.canDownload && (
                              <Button
                                onClick={() => downloadDoc()}
                                className="border-0"
                                icon={
                                  <span>
                                    <DownloadOutlined />
                                  </span>
                                }
                              />
                            )}
                            <Button
                              onClick={() => openDocument()}
                              className="border-0"
                              icon={
                                <span>
                                  <ExportOutlined />
                                </span>
                              }
                            />
                            {permissions.canPrint && (
                              <Button
                                onClick={() => onClickPrint()}
                                className="border-0"
                                icon={
                                  <span>
                                    <PrinterOutlined />
                                  </span>
                                }
                              />
                            )}
                          </div>
                        </div>
                        {isRejectEnable && status === "Pending" && (
                          <div className="remark-section mb-2">
                            <strong className="fs-3">Remarks:</strong>
                            <Input
                              value={rejectionReason}
                              onChange={(e) => {
                                console.log(e.target.value);
                                setRejectionReason(e.target.value);
                              }}
                              placeholder="Enter your reason..."
                            />
                            <button
                              disabled={
                                rejectionReason?.trim() === "" ||
                                rejectionReason === null
                              }
                              className={`btn-sm ${rejectionReason?.trim() === "" ||
                                rejectionReason === null
                                ? "btn-secondary"
                                : "btn-primary"
                                } __submit`}
                              onClick={() => documentStatusMark(false)}
                            >
                              Submit
                            </button>
                            <button
                              className="btn-sm __cancel"
                              onClick={() => {
                                setRejectionReason(null);
                                setIsRejectEnable(false);
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                        <MetaDataTab
                          candidateDetails={candidateDetails}
                          DOB={DOB}
                          activeDocData={activeDocData}
                          docVerification={docVerification}
                        />
                        {/* <div
                          className="text-sm mb-2 px-3 py-2 flex justify-content-between"
                          style={{
                            backgroundColor: "#EBEBEB",
                            justifyContent: "space-between",
                            border: "1px solid #C9C9C9",
                          }}
                        >
                          <span
                            style={{
                              flex: "1 1",
                              borderRight: "1px solid black",
                              textAlign: "center",
                            }}
                          >
                            <span className="font-medium">File Name:</span>{" "}
                            {activeDocData?.docData?.filename}
                          </span>
                          <span
                            style={{
                              flex: "1 1",
                              borderRight: "1px solid black",
                              textAlign: "center",
                            }}
                          >
                            <span className="font-medium">Type:</span>{" "}
                            {activeDocData?.docData?.mimetype}
                          </span>
                          <span
                            style={{
                              flex: "1 1",
                              // borderRight: "1px solid black",
                              textAlign: "center",
                            }}
                          >
                            <span className="font-medium">Size:</span>{" "}
                            {size ? size : ""}
                          </span>
                          <span style={{ flex: "1 1", textAlign: "center" }}>
                            <span className="font-medium">Uploaded:</span> March
                            26 4:56 PM
                          </span>
                        </div> */}
                        {IS_DOCUMENT_REJECTED && (
                          <>
                            {ACCEPT_REASON_BY_SITE_HR !== "" && (
                              <div
                                className="mb-2"
                                style={{
                                  height: "33px",
                                  background: "#FCFEFC",
                                  fontWeight: "bold",
                                  border: "1px solid #00AC1A",
                                  padding: "5px 10px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                }}
                              >
                                <div
                                  style={{
                                    gap: "0.7rem",
                                    display: "flex",
                                    alignItems: "center",
                                    color: "#00AC1A",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontWeight: "bold",
                                      color: "#000",
                                    }}
                                  >
                                    {SITE_HR_NAME} {"(Site HR)"} :{" "}
                                  </span>
                                  <span>
                                    <WarningFilled
                                      style={{ fontSize: "1.1rem" }}
                                    />
                                  </span>
                                  <span>{ACCEPT_REASON_BY_SITE_HR}</span>
                                </div>
                                {activeDocData?.auditingDetails
                                  ?.acceptanceRemarksByMaker !== null && (
                                    <Tooltip
                                      placement="top"
                                      title={
                                        <div>
                                          {activeDocData?.auditingDetails?.acceptanceRemarksByMaker?.map(
                                            (ele, index) => {
                                              return (
                                                <div>
                                                  {index + 1} -&gt; {ele}
                                                </div>
                                              );
                                            },
                                          )}
                                        </div>
                                      }
                                    >
                                      <span
                                        style={{
                                          display: "flex",
                                          cursor: "pointer",
                                          backgroundColor: "#00AC1A",
                                          width: "20px",
                                          color: "#fff",
                                          borderRadius: "50%",
                                          alignItems: "center",
                                          justifyContent: "center",
                                        }}
                                      >
                                        {ACCEPT_REASON_BY_SITE_HR_COUNT}
                                      </span>
                                    </Tooltip>
                                  )}
                              </div>
                            )}
                            {REASON_BY_SITE_HR !== "" && (
                              <div
                                className="mb-2"
                                style={{
                                  height: "33px",
                                  background: "#FFF8F8",
                                  fontWeight: "bold",
                                  border: "1px solid #FFC3C3",
                                  padding: "5px 10px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                }}
                              >
                                <div
                                  style={{
                                    gap: "0.7rem",
                                    display: "flex",
                                    alignItems: "center",
                                    color: "#FF0000",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontWeight: "bold",
                                      color: "#000",
                                    }}
                                  >
                                    {SITE_HR_NAME} {"(Site HR)"} :{" "}
                                  </span>
                                  <span>
                                    <WarningFilled
                                      style={{ fontSize: "1.1rem" }}
                                    />
                                  </span>
                                  <span>{REASON_BY_SITE_HR}</span>
                                </div>
                                {activeDocData?.auditingDetails
                                  ?.rejectionRemarksByMaker !== null && (
                                    <Tooltip
                                      placement="top"
                                      title={
                                        <div>
                                          {activeDocData?.auditingDetails?.rejectionRemarksByMaker?.map(
                                            (ele, index) => {
                                              return (
                                                <div>
                                                  {index + 1} -&gt; {ele}
                                                </div>
                                              );
                                            },
                                          )}
                                        </div>
                                      }
                                    >
                                      <span
                                        style={{
                                          display: "flex",
                                          cursor: "pointer",
                                          backgroundColor: "#FF0000",
                                          width: "20px",
                                          color: "#fff",
                                          borderRadius: "50%",
                                          alignItems: "center",
                                          justifyContent: "center",
                                        }}
                                      >
                                        {REASON_BY_SITE_HR_COUNT}
                                      </span>
                                    </Tooltip>
                                  )}
                              </div>
                            )}
                            {REASON_BY_CENTRAL_HR !== "" && (
                              <div
                                className="mb-2"
                                style={{
                                  height: "33px",
                                  cursor: "pointer",
                                  background: "#FFF8F8",
                                  fontWeight: "bold",
                                  border: "1px solid #FFC3C3",
                                  padding: "5px 10px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                }}
                              >
                                <div
                                  style={{
                                    gap: "0.7rem",
                                    display: "flex",
                                    alignItems: "center",
                                    color: "#FF0000",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontWeight: "bold",
                                      color: "#000",
                                    }}
                                  >
                                    {CENTRAL_HR_NAME} {"(Central HR)"} :{" "}
                                  </span>
                                  <span>
                                    <WarningFilled
                                      style={{ fontSize: "1.1rem" }}
                                    />
                                  </span>
                                  <span>{REASON_BY_CENTRAL_HR}</span>
                                </div>
                                {activeDocData?.auditingDetails
                                  ?.rejectionRemarksByChecker !== null && (
                                    <Tooltip
                                      placement="top"
                                      title={
                                        <div>
                                          {activeDocData?.auditingDetails?.rejectionRemarksByChecker?.map(
                                            (ele, index) => {
                                              return (
                                                <div>
                                                  {index + 1} -&gt; {ele}
                                                </div>
                                              );
                                            },
                                          )}
                                        </div>
                                      }
                                    >
                                      <span
                                        style={{
                                          display: "flex",
                                          backgroundColor: "#FF0000",
                                          width: "20px",
                                          color: "#fff",
                                          borderRadius: "50%",
                                          alignItems: "center",
                                          justifyContent: "center",
                                        }}
                                      >
                                        {REASON_BY_CENTRAL_HR_COUNT}
                                      </span>
                                    </Tooltip>
                                  )}
                              </div>
                            )}
                          </>
                        )}
                      </>
                    )}
                    <div
                      className="relative border rounded"
                      style={{ minHeight: "500px", position: "relative" }}
                    >
                      <div
                        style={{
                          height: "500px",
                          overflowY: "auto",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <RenderFiles
                          src={activeDocData?.path}
                          ext={docExtension}
                        />
                      </div>
                      <div
                        onClick={() => {
                          setIsRejectEnable(false);
                          setRejectionReason(null);
                          if (activeDoc === 1) return;
                          setActiveDoc(activeDoc - 1);
                        }}
                        style={{
                          position: "absolute",
                          top: "50%",
                          transform: "translateY(-50%)",
                          left: -25,
                          background: "#80808000",
                          fontSize: "3.5rem",
                          borderRadius: "50%",
                          width: "50px",
                          height: "50px",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          cursor: "pointer",
                          color: "#6B6B6B",
                        }}
                      >
                        <LeftOutlined />
                      </div>
                      <div
                        onClick={() => {
                          setIsRejectEnable(false);
                          setRejectionReason(null);
                          if (filteredDoc?.length === activeDoc) return;
                          setActiveDoc(activeDoc + 1);
                        }}
                        style={{
                          cursor: "pointer",
                          position: "absolute",
                          top: "50%",
                          transform: "translateY(-50%)",
                          right: -25,
                          background: "#80808000",
                          fontSize: "3.5rem",
                          borderRadius: "50%",
                          width: "50px",
                          height: "50px",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          color: "#6B6B6B",
                        }}
                      >
                        <RightOutlined />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    className="border rounded p-4"
                    style={{
                      minHeight: "750px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Title level={3} className="mb-1 fw-bold">
                      No Document Found
                    </Title>
                  </div>
                )}
              </Col>
            </Row>
          </Card>
        </div>
      </div>
    </Dashboard>
  );
};

export default CentralHrAuditPage;
